import { ChangeDetectionStrategy, Component, input, inject, signal, computed, DestroyRef, effect, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';

import { AuthService } from '../../../core/auth/auth.service';
import { SiteService } from '../../../core/services/site.service';
import { SensorManageService } from '../../../core/services/sensor-manage.service';
import { StorageService } from '../../../core/services/storage.service';
import { SiteDrawingCanvasComponent } from '../site-drawing-canvas/site-drawing-canvas';
import { DialogService } from '../../../core/services/dialog.service';
import { firstValueFrom } from 'rxjs';
import type { Sensor } from '../../../shared/interfaces';
import { normalizeNorthAngle } from '../../../shared/utils/wind-direction';

@Component({
  selector: 'app-site-drawing-manage',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule, 
    MatIconModule, 
    MatProgressBarModule,
    MatProgressSpinnerModule,
    SiteDrawingCanvasComponent
  ],
  templateUrl: './site-drawing-manage.html',
})
export class SiteDrawingManageComponent {
  readonly compassTicks = Array.from({ length: 24 }, (_, index) => index * 15);
  siteId = input.required<string>();

  private readonly authService = inject(AuthService);
  private readonly siteService = inject(SiteService);
  private readonly sensorManageService = inject(SensorManageService);
  private readonly storageService = inject(StorageService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialogService = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);

  site = computed(() => this.siteService.sites().find(s => s.id === this.siteId()));
  drawingUrl = computed(() => this.site()?.drawingUrl);
  allSensors = this.sensorManageService.allSensors;
  
  originalSiteSensors = computed(() => this.allSensors().filter(s => s.siteId === this.siteId()));

  modifiedPositions = signal<Map<string, { x: number; y: number }>>(new Map());
  northAngle = signal(0);
  isNorthAngleDragging = signal(false);
  private readonly savedNorthAngle = signal(0);

  siteSensors = computed(() => {
    const modifications = this.modifiedPositions();
    return this.originalSiteSensors().map(sensor => {
      if (modifications.has(sensor.id)) {
        return { ...sensor, drawingPosition: modifications.get(sensor.id) } as Sensor;
      }
      return sensor;
    });
  });

  hasDrawingPositionChanges = computed(() => this.modifiedPositions().size > 0);
  hasNorthAngleChanged = computed(() => this.northAngle() !== this.savedNorthAngle());
  hasChanges = computed(() => this.hasDrawingPositionChanges() || this.hasNorthAngleChanged());

  isUploading = signal(false);
  uploadProgress = signal(0);
  isSaving = signal(false);

  isLoading = computed(() => this.siteService.isLoading() || this.sensorManageService.isLoading() || this.isSaving());

  constructor() {
    effect(() => {
      const site = this.site();
      const serverAngle = normalizeNorthAngle(site?.northAngle);

      untracked(() => {
        const currentSaved = this.savedNorthAngle();
        if (serverAngle !== currentSaved) {
          if (this.northAngle() === currentSaved) {
            this.northAngle.set(serverAngle);
          }
          this.savedNorthAngle.set(serverAngle);
        }
      });
    });
  }

  onPositionChanged(event: { sensorId: string; position: { x: number; y: number } }) {
    this.modifiedPositions.update(map => {
      const newMap = new Map(map);
      newMap.set(event.sensorId, event.position);
      return newMap;
    });
  }

  setNorthAngle(angle: unknown): void {
    this.northAngle.set(normalizeNorthAngle(angle));
  }

  onNorthAngleInput(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    this.setNorthAngle(inputEl.value);
  }

  onNorthAngleKeydown(event: KeyboardEvent): void {
    const keyActions: Record<string, number | 'start' | 'end'> = {
      ArrowUp: 1,
      ArrowRight: 1,
      ArrowDown: -1,
      ArrowLeft: -1,
      PageUp: 15,
      PageDown: -15,
      Home: 'start',
      End: 'end',
    };
    const action = keyActions[event.key];
    if (action === undefined) return;

    event.preventDefault();
    if (action === 'start') {
      this.setNorthAngle(0);
      return;
    }
    if (action === 'end') {
      this.setNorthAngle(359);
      return;
    }
    this.setNorthAngle(this.northAngle() + action);
  }

  startNorthAngleDrag(event: PointerEvent, compass: HTMLElement): void {
    this.isNorthAngleDragging.set(true);
    compass.setPointerCapture(event.pointerId);
    this.updateNorthAngleFromPointer(event, compass);
  }

  continueNorthAngleDrag(event: PointerEvent, compass: HTMLElement): void {
    if (!this.isNorthAngleDragging()) return;
    this.updateNorthAngleFromPointer(event, compass);
  }

  stopNorthAngleDrag(event: PointerEvent, compass: HTMLElement): void {
    this.isNorthAngleDragging.set(false);
    if (compass.hasPointerCapture(event.pointerId)) {
      compass.releasePointerCapture(event.pointerId);
    }
  }

  private updateNorthAngleFromPointer(event: PointerEvent, compass: HTMLElement): void {
    const rect = compass.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const radians = Math.atan2(event.clientY - centerY, event.clientX - centerX);
    const degreesFromNorth = radians * 180 / Math.PI + 90;
    this.setNorthAngle(degreesFromNorth);
  }

  async onFileSelected(event: Event) {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (!file) return;

    fileInput.value = '';

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.snackBar.open('対応していないファイル形式です。JPEG, PNG, WebPを使用してください。', '閉じる', { duration: 3000 });
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      this.snackBar.open('ファイルサイズは5MB以下にしてください。', '閉じる', { duration: 3000 });
      return;
    }

    const siteId = this.siteId();
    const orgId = this.authService.userOrganizationId() ?? this.site()?.organizationId;
    if (!siteId || !orgId) {
      this.snackBar.open('組織情報が取得できません', '閉じる', { duration: 3000 });
      return;
    }

    this.isUploading.set(true);
    this.uploadProgress.set(0);

    const path = `organizations/${orgId}/sites/${siteId}/drawings/${Date.now()}_${file.name}`;
    
    try {
      const { progress$ } = this.storageService.uploadFile(path, file);
      
      progress$.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          this.uploadProgress.set(Math.round(progress));
        },
        error: (error) => {
          console.error('Upload failed:', error);
          this.snackBar.open('画像のアップロードに失敗しました', '閉じる', { duration: 3000 });
          this.isUploading.set(false);
        },
        complete: async () => {
          try {
            const url = await this.storageService.getDownloadUrl(path);
            await this.siteService.updateSite(siteId, { drawingUrl: url });
            this.siteService.reloadSites();
            this.snackBar.open('配置図を更新しました', '閉じる', { duration: 3000 });
          } catch (e) {
            console.error('URL save failed:', e);
            this.snackBar.open('配置図URLの保存に失敗しました', '閉じる', { duration: 3000 });
          } finally {
            this.isUploading.set(false);
          }
        }
      });
    } catch (err) {
      console.error('Upload start failed:', err);
      this.snackBar.open('画像のアップロード開始に失敗しました', '閉じる', { duration: 3000 });
      this.isUploading.set(false);
    }
  }

  async saveChanges() {
    if (!this.hasChanges()) return;

    const modifications = this.modifiedPositions();
    const updates = Array.from(modifications.entries()).map(([sensorId, position]) => ({
      sensorId,
      position
    }));
    const shouldSavePositions = updates.length > 0;
    const shouldSaveNorthAngle = this.hasNorthAngleChanged();
    const northAngleToSave = this.northAngle();

    this.isSaving.set(true);
    try {
      const promises: Promise<unknown>[] = [];
      if (shouldSavePositions) {
        promises.push(this.sensorManageService.updateDrawingPositions(updates));
      }
      if (shouldSaveNorthAngle) {
        promises.push(this.siteService.updateSite(this.siteId(), { northAngle: northAngleToSave }));
      }

      await Promise.all(promises);

      if (shouldSavePositions) {
        this.modifiedPositions.set(new Map());
      }
      if (shouldSaveNorthAngle) {
        this.savedNorthAngle.set(northAngleToSave);
        this.siteService.reloadSites();
      }

      const message = shouldSavePositions && shouldSaveNorthAngle
        ? '配置位置と北方位を保存しました'
        : shouldSaveNorthAngle
          ? '北方位を保存しました'
          : '配置位置を保存しました';
      this.snackBar.open(message, '閉じる', { duration: 3000 });
    } catch (error) {
      this.snackBar.open('変更の保存に失敗しました', '閉じる', { duration: 3000 });
    } finally {
      this.isSaving.set(false);
    }
  }

  async saveNorthAngle(): Promise<void> {
    if (!this.hasNorthAngleChanged()) return;

    this.isSaving.set(true);
    try {
      await this.siteService.updateSite(this.siteId(), { northAngle: this.northAngle() });
      this.savedNorthAngle.set(this.northAngle());
      this.siteService.reloadSites();
      this.snackBar.open('北方位を保存しました', '閉じる', { duration: 3000 });
    } catch (error) {
      this.snackBar.open('北方位の保存に失敗しました', '閉じる', { duration: 3000 });
    } finally {
      this.isSaving.set(false);
    }
  }

  async deleteDrawing() {
    const url = this.drawingUrl();
    const siteId = this.siteId();
    if (!url || !siteId) return;

    const confirmed = await firstValueFrom(this.dialogService.confirm({
      title: '確認',
      message: '図面画像を削除してもよろしいですか？\n配置したセンサーの位置情報もリセットされます。',
      confirmText: '削除',
      cancelText: 'キャンセル',
      color: 'warn'
    }));

    if (!confirmed) return;

    this.isSaving.set(true);
    try {
      // 1. Storageから画像を削除
      await this.storageService.deleteFile(url);
      
      // 2. 現場情報の drawingUrl をクリア
      await this.siteService.updateSite(siteId, { drawingUrl: null });
      
      // 3. センサーの配置位置をリセット
      const siteSensors = this.originalSiteSensors().filter(s => s.drawingPosition);
      if (siteSensors.length > 0) {
        const updates = siteSensors.map(s => ({ sensorId: s.id, position: null }));
        await this.sensorManageService.updateDrawingPositions(updates);
      }
      
      this.modifiedPositions.set(new Map());
      this.siteService.reloadSites();
      this.snackBar.open('図面を削除しました', '閉じる', { duration: 3000 });
    } catch (error) {
      console.error('Delete drawing failed:', error);
      this.snackBar.open('図面の削除に失敗しました', '閉じる', { duration: 3000 });
    } finally {
      this.isSaving.set(false);
    }
  }
}
